function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function textValue(value) {
  return String(value ?? '').trim();
}

function normalizedNickname(value) {
  return textValue(value).replace(/\s+/g, ' ').toLocaleLowerCase('ko-KR');
}

function decodeMaybeBase64(value) {
  const text = textValue(value);
  if (!text || typeof globalThis.atob !== 'function') return '';
  try {
    const normalized = text.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return globalThis.atob(padded);
  } catch {
    return '';
  }
}

function normalizedOpenid(value) {
  const raw = textValue(value);
  if (!raw) return '';
  for (const candidate of [raw, decodeMaybeBase64(raw)]) {
    if (!candidate) continue;
    const hyphen = candidate.match(/^\d+-([0-9]{8,})$/);
    if (hyphen) return hyphen[1];
    if (/^[0-9]{8,}$/.test(candidate)) return candidate;
    const groups = candidate.match(/[0-9]{8,}/g);
    if (groups?.length) return groups.at(-1);
  }
  return raw;
}

function stableDeckKey(deck) {
  const items = Array.isArray(deck) ? deck : [];
  return items
    .map((item, index) => {
      if (typeof item === 'string' || typeof item === 'number') return `${index + 1}:${textValue(item)}`;
      const code = textValue(item?.code ?? item?.nikke_id ?? item?.id ?? item?.name);
      const level = numberValue(item?.level ?? item?.char_level, 0);
      const slot = numberValue(item?.slot, index + 1);
      return `${slot}:${code}:${level}`;
    })
    .filter(Boolean)
    .join('|');
}

function rawMemberKey(record) {
  return textValue(record?.member_openid ?? record?.memberOpenid ?? record?.openid ?? record?.member_nickname ?? record?.nickname);
}

function nicknameOf(record) {
  return textValue(record?.member_nickname ?? record?.memberNickname ?? record?.nickname);
}

function canonicalMemberMap(records) {
  const byNickname = new Map();
  for (const record of records) {
    const nicknameKey = normalizedNickname(nicknameOf(record));
    if (!nicknameKey) continue;
    const rawKey = rawMemberKey(record);
    const normalizedKey = normalizedOpenid(rawKey);
    const candidate = normalizedKey || rawKey;
    const current = byNickname.get(nicknameKey);
    if (!current || (/^[0-9]{8,}$/.test(candidate) && !/^[0-9]{8,}$/.test(current))) byNickname.set(nicknameKey, candidate);
  }
  return byNickname;
}

function recordMemberKey(record, nicknameMap = null) {
  const nicknameKey = normalizedNickname(nicknameOf(record));
  if (nicknameMap && nicknameKey && nicknameMap.has(nicknameKey)) return nicknameMap.get(nicknameKey);
  const raw = rawMemberKey(record);
  return normalizedOpenid(raw) || raw;
}

function recordUpdatedAt(record) {
  return Date.parse(record?.updated_at ?? record?.updatedAt ?? record?.created_at ?? record?.createdAt ?? '') || 0;
}

function recordRichness(record) {
  return (textValue(record?.source_record_id ?? record?.sourceRecordId) ? 2 : 0)
    + (textValue(record?.attacked_at ?? record?.attackedAt) ? 2 : 0)
    + (stableDeckKey(record?.deck) ? 2 : 0)
    + (nicknameOf(record) ? 1 : 0)
    + (numberValue(record?.char_level ?? record?.charLevel, 0) > 0 ? 1 : 0);
}

function preferRecord(current, candidate) {
  if (!current) return candidate;
  const currentScore = recordRichness(current);
  const candidateScore = recordRichness(candidate);
  if (candidateScore !== currentScore) return candidateScore > currentScore ? candidate : current;
  return recordUpdatedAt(candidate) >= recordUpdatedAt(current) ? candidate : current;
}

function signatureKey(record, memberKey) {
  return [
    memberKey,
    numberValue(record?.boss_no ?? record?.bossNo),
    numberValue(record?.phase_no ?? record?.phaseNo),
    numberValue(record?.damage_eok ?? record?.damageEok).toFixed(6),
    Boolean(record?.is_kill ?? record?.isKill) ? '1' : '0',
    stableDeckKey(record?.deck),
  ].join('::');
}

export function dedupeRaidOverviewRecords(records = []) {
  const source = Array.isArray(records) ? records : [];
  const nicknameMap = canonicalMemberMap(source);
  const grouped = new Map();

  for (const record of source) {
    const memberKey = recordMemberKey(record, nicknameMap);
    const bossNo = numberValue(record?.boss_no ?? record?.bossNo);
    const phaseNo = numberValue(record?.phase_no ?? record?.phaseNo);
    const damage = numberValue(record?.damage_eok ?? record?.damageEok, 0);
    if (!memberKey || bossNo < 1 || bossNo > 5 || phaseNo < 1 || phaseNo > 4 || damage <= 0) continue;
    const list = grouped.get(memberKey) || [];
    list.push({ ...record, __memberKey: memberKey });
    grouped.set(memberKey, list);
  }

  const memberGroups = [...grouped.entries()]
    .map(([memberKey, memberRecords]) => ({ memberKey, memberRecords }))
    .sort((a, b) => b.memberRecords.length - a.memberRecords.length || a.memberKey.localeCompare(b.memberKey))
    .slice(0, 32);

  const output = [];
  for (const { memberKey, memberRecords } of memberGroups) {
    const byAttackNo = new Map();
    const bySignature = new Map();
    for (const record of memberRecords) {
      const attackNo = numberValue(record?.attack_no ?? record?.attackNo, 0);
      if (attackNo >= 1 && attackNo <= 3) {
        byAttackNo.set(attackNo, preferRecord(byAttackNo.get(attackNo), record));
      } else {
        const key = signatureKey(record, memberKey);
        bySignature.set(key, preferRecord(bySignature.get(key), record));
      }
    }

    const chosen = [...byAttackNo.entries()].map(([attackNo, record]) => ({ ...record, attack_no: attackNo }));
    const chosenSignatures = new Set(chosen.map((record) => signatureKey(record, memberKey)));
    const remaining = [...bySignature.values()]
      .filter((record) => !chosenSignatures.has(signatureKey(record, memberKey)))
      .sort((a, b) => recordUpdatedAt(a) - recordUpdatedAt(b)
        || numberValue(a?.boss_no ?? a?.bossNo) - numberValue(b?.boss_no ?? b?.bossNo)
        || numberValue(a?.phase_no ?? a?.phaseNo) - numberValue(b?.phase_no ?? b?.phaseNo)
        || numberValue(a?.damage_eok ?? a?.damageEok) - numberValue(b?.damage_eok ?? b?.damageEok)
        || stableDeckKey(a?.deck).localeCompare(stableDeckKey(b?.deck)));

    const used = new Set(chosen.map((record) => numberValue(record.attack_no, 0)));
    for (const record of remaining) {
      const attackNo = [1, 2, 3].find((slot) => !used.has(slot));
      if (!attackNo) break;
      used.add(attackNo);
      chosen.push({ ...record, attack_no: attackNo });
    }

    chosen
      .sort((a, b) => numberValue(a.attack_no) - numberValue(b.attack_no))
      .slice(0, 3)
      .forEach((record) => output.push({ ...record, member_openid: memberKey }));
  }

  return output.slice(0, 96);
}

function phaseConfigMap(phases = []) {
  const map = new Map();
  for (const phase of Array.isArray(phases) ? phases : []) {
    const bossNo = numberValue(phase?.boss_no ?? phase?.bossNo);
    const phaseNo = numberValue(phase?.phase_no ?? phase?.phaseNo);
    if (bossNo < 1 || bossNo > 5 || phaseNo < 1 || phaseNo > 4) continue;
    map.set(`${bossNo}:${phaseNo}`, {
      maxHpEok: phase?.max_hp_eok === null || phase?.maxHpEok === null
        ? null
        : numberValue(phase?.max_hp_eok ?? phase?.maxHpEok, 0),
      isInfinite: Boolean(phase?.is_infinite ?? phase?.isInfinite),
    });
  }
  return map;
}

function groupRecordsByBossPhase(records = []) {
  const map = new Map();
  for (const record of records) {
    const bossNo = numberValue(record?.boss_no ?? record?.bossNo);
    const phaseNo = numberValue(record?.phase_no ?? record?.phaseNo);
    const key = `${bossNo}:${phaseNo}`;
    const list = map.get(key) || [];
    list.push(record);
    map.set(key, list);
  }
  return map;
}

function buildPhaseModel(bossNo, phaseNo, grouped, configMap) {
  const rows = grouped.get(`${bossNo}:${phaseNo}`) || [];
  const damageEok = rows.reduce((sum, row) => sum + numberValue(row?.damage_eok ?? row?.damageEok, 0), 0);
  const config = configMap.get(`${bossNo}:${phaseNo}`) || { maxHpEok: null, isInfinite: false };
  const isInfinite = Boolean(config.isInfinite || (phaseNo === 4 && bossNo === 5));
  const maxHpEok = isInfinite ? null : config.maxHpEok;
  const isKill = rows.some((row) => Boolean(row?.is_kill ?? row?.isKill));
  const remainingHpEok = isInfinite ? null : Math.max(numberValue(maxHpEok, 0) - damageEok, 0);
  const cleared = phaseNo <= 3 && Boolean(
    isKill || (maxHpEok !== null && maxHpEok > 0 && remainingHpEok <= 0.000001),
  );
  const remainingPercent = isInfinite
    ? 100
    : maxHpEok && maxHpEok > 0
      ? Math.max(0, Math.min(100, (remainingHpEok / maxHpEok) * 100))
      : 0;
  const progressPercent = isInfinite ? 100 : Math.max(0, Math.min(100, 100 - remainingPercent));
  const lastAttackAt = rows
    .map((row) => row?.attacked_at ?? row?.attackedAt ?? row?.updated_at ?? row?.updatedAt ?? null)
    .filter(Boolean)
    .sort()
    .at(-1) || null;

  return {
    bossNo,
    phaseNo,
    rows,
    maxHpEok,
    damageEok,
    remainingHpEok,
    remainingPercent,
    progressPercent,
    attackCount: rows.length,
    cleared,
    isInfinite,
    lastAttackAt,
  };
}

export function buildRaidBossOverview({ bosses = [], phases = [], records = [] } = {}) {
  const cleanRecords = dedupeRaidOverviewRecords(records);
  const grouped = groupRecordsByBossPhase(cleanRecords);
  const configMap = phaseConfigMap(phases);
  const normalizedBosses = (Array.isArray(bosses) ? bosses : [])
    .map((boss) => ({
      bossNo: numberValue(boss?.boss_no ?? boss?.bossNo),
      bossName: textValue(boss?.boss_name ?? boss?.bossName),
      element: textValue(boss?.element),
      imageUrl: textValue(boss?.image_url ?? boss?.imageUrl),
      comment: textValue(boss?.boss_comment ?? boss?.comment),
      coefficient: numberValue(boss?.coefficient, 1),
    }))
    .filter((boss) => boss.bossNo >= 1 && boss.bossNo <= 5)
    .sort((a, b) => a.bossNo - b.bossNo);

  const phaseModelsByBoss = new Map();
  for (const boss of normalizedBosses) {
    phaseModelsByBoss.set(
      boss.bossNo,
      [1, 2, 3, 4].map((phaseNo) => buildPhaseModel(boss.bossNo, phaseNo, grouped, configMap)),
    );
  }

  let globalPhase = 1;
  for (let phaseNo = 1; phaseNo <= 3; phaseNo += 1) {
    const allCleared = normalizedBosses.length === 5
      && normalizedBosses.every((boss) => phaseModelsByBoss.get(boss.bossNo)?.find((phase) => phase.phaseNo === phaseNo)?.cleared);
    if (!allCleared) {
      globalPhase = phaseNo;
      break;
    }
    globalPhase = phaseNo === 3 ? 4 : phaseNo + 1;
  }

  const bossModels = normalizedBosses.map((boss) => {
    const phaseModels = phaseModelsByBoss.get(boss.bossNo) || [];
    const currentPhase = globalPhase === 4 ? (boss.bossNo === 5 ? 4 : 3) : globalPhase;
    const current = phaseModels.find((phase) => phase.phaseNo === currentPhase) || phaseModels[0];
    const totalRows = cleanRecords.filter((record) => numberValue(record?.boss_no ?? record?.bossNo) === boss.bossNo);
    const totalDamageEok = totalRows.reduce((sum, record) => sum + numberValue(record?.damage_eok ?? record?.damageEok, 0), 0);
    const averageDamageEok = totalRows.length ? totalDamageEok / totalRows.length : 0;
    const isActive = globalPhase <= 3 || boss.bossNo === 5;
    const isInfinite = Boolean(globalPhase === 4 && boss.bossNo === 5);
    const isCleared = !isActive || Boolean(globalPhase <= 3 && current?.cleared);
    const lastAttackAt = totalRows
      .map((record) => record?.attacked_at ?? record?.attackedAt ?? record?.updated_at ?? record?.updatedAt ?? null)
      .filter(Boolean)
      .sort()
      .at(-1) || null;

    return {
      ...boss,
      phases: phaseModels,
      currentPhase,
      maxHpEok: isInfinite ? null : current?.maxHpEok ?? null,
      currentDamageEok: current?.damageEok || 0,
      remainingHpEok: isInfinite ? null : isActive ? current?.remainingHpEok ?? 0 : 0,
      remainingPercent: isInfinite ? 100 : isActive ? current?.remainingPercent ?? 0 : 0,
      progressPercent: isInfinite ? 100 : isActive ? current?.progressPercent ?? 100 : 100,
      totalDamageEok,
      averageDamageEok,
      attackCount: totalRows.length,
      isInfinite,
      isActive,
      isCleared,
      basePhasesCleared: phaseModels.filter((phase) => phase.phaseNo <= 3 && phase.cleared).length,
      lastAttackAt,
    };
  });

  const currentStageTotalHpEok = globalPhase === 4
    ? null
    : bossModels.reduce((sum, boss) => sum + numberValue(boss.maxHpEok, 0), 0);
  const currentStageRemainingHpEok = globalPhase === 4
    ? null
    : bossModels.reduce((sum, boss) => sum + numberValue(boss.remainingHpEok, 0), 0);
  const stageProgressPercent = globalPhase === 4
    ? 100
    : currentStageTotalHpEok > 0
      ? Math.max(0, Math.min(100, 100 - (currentStageRemainingHpEok / currentStageTotalHpEok) * 100))
      : 0;

  return {
    globalPhase,
    records: cleanRecords,
    bosses: bossModels,
    currentStageTotalHpEok,
    currentStageRemainingHpEok,
    stageProgressPercent,
  };
}

export function buildRaidSeasonMembers({ records = [], fallbackMembers = [], maxMembers = 32 } = {}) {
  const cleanRecords = dedupeRaidOverviewRecords(records);
  const map = new Map();
  for (const record of cleanRecords) {
    const openid = recordMemberKey(record);
    if (!openid) continue;
    const current = map.get(openid) || {
      openid,
      nickname: nicknameOf(record) || '닉네임 확인 불가',
      attackCount: 0,
      remainingAttacks: 3,
      totalDamageEok: 0,
      lastAttackAt: null,
      hasSeasonRecord: true,
    };
    current.attackCount += 1;
    current.remainingAttacks = Math.max(3 - Math.min(current.attackCount, 3), 0);
    current.totalDamageEok += numberValue(record?.damage_eok ?? record?.damageEok, 0);
    const attackAt = record?.attacked_at ?? record?.attackedAt ?? record?.updated_at ?? record?.updatedAt ?? null;
    if (attackAt && (!current.lastAttackAt || String(attackAt) > String(current.lastAttackAt))) current.lastAttackAt = attackAt;
    const nickname = nicknameOf(record);
    if (nickname) current.nickname = nickname;
    map.set(openid, current);
  }

  const fallback = (Array.isArray(fallbackMembers) ? fallbackMembers : [])
    .map((member) => ({
      openid: normalizedOpenid(member?.member_openid ?? member?.openid) || textValue(member?.member_openid ?? member?.openid),
      nickname: textValue(member?.nickname) || '닉네임 확인 불가',
      attackCount: numberValue(member?.attack_count ?? member?.attackCount, 0),
      remainingAttacks: numberValue(member?.remaining_attacks ?? member?.remainingAttacks, 3),
      totalDamageEok: numberValue(member?.total_damage_eok ?? member?.totalDamageEok, 0),
      lastAttackAt: member?.last_attack_at ?? member?.lastAttackAt ?? null,
      hasSeasonRecord: numberValue(member?.attack_count ?? member?.attackCount, 0) > 0,
    }))
    .filter((member) => member.openid);

  for (const member of fallback) {
    if (map.has(member.openid)) continue;
    const nicknameKey = normalizedNickname(member.nickname);
    const duplicate = [...map.values()].some((existing) => normalizedNickname(existing.nickname) === nicknameKey);
    if (!duplicate) map.set(member.openid, member);
  }

  return [...map.values()]
    .sort((a, b) => Number(b.hasSeasonRecord) - Number(a.hasSeasonRecord)
      || b.attackCount - a.attackCount
      || b.totalDamageEok - a.totalDamageEok
      || a.nickname.localeCompare(b.nickname, 'ko'))
    .slice(0, Math.max(1, Math.min(32, numberValue(maxMembers, 32))))
    .sort((a, b) => b.remainingAttacks - a.remainingAttacks
      || b.totalDamageEok - a.totalDamageEok
      || a.nickname.localeCompare(b.nickname, 'ko'));
}

export function buildRaidLobbySummary({ bossOverview, members = [] } = {}) {
  const records = Array.isArray(bossOverview?.records) ? bossOverview.records : [];
  const memberRows = Array.isArray(members) ? members : [];
  const memberCount = Math.min(32, memberRows.length);
  const recordCount = records.length;
  const totalAttackLimit = memberCount * 3;
  const remainingAttacks = Math.max(0, totalAttackLimit - recordCount);
  const totalDamageEok = records.reduce((sum, record) => sum + numberValue(record?.damage_eok ?? record?.damageEok, 0), 0);
  const topDealers = memberRows
    .filter((member) => member.attackCount > 0)
    .slice()
    .sort((a, b) => b.totalDamageEok - a.totalDamageEok || a.nickname.localeCompare(b.nickname, 'ko'))
    .slice(0, 5);
  const attackGroups = {
    done: memberRows.filter((member) => member.attackCount >= 3).sort((a, b) => a.nickname.localeCompare(b.nickname, 'ko')),
    partial: memberRows.filter((member) => member.attackCount > 0 && member.attackCount < 3).sort((a, b) => a.nickname.localeCompare(b.nickname, 'ko')),
    none: memberRows.filter((member) => member.attackCount === 0).sort((a, b) => a.nickname.localeCompare(b.nickname, 'ko')),
  };

  return {
    memberCount,
    recordCount,
    totalAttackLimit,
    remainingAttacks,
    totalDamageEok,
    topDealers,
    attackGroups,
    currentStageTotalHpEok: bossOverview?.currentStageTotalHpEok ?? null,
    currentStageRemainingHpEok: bossOverview?.currentStageRemainingHpEok ?? null,
    stageProgressPercent: numberValue(bossOverview?.stageProgressPercent, 0),
  };
}
